import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle } from 'lucide-react';

const Widerruf = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Header */}
      <nav className="px-6 lg:px-12 py-6 border-b border-white/10">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2">
            <img src="/revocut-logo.png" alt="RevoCut" className="w-10 h-10" />
            <span className="text-xl font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>REVOCUT</span>
          </button>
          <button 
            onClick={() => navigate(-1)}
            className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück
          </button>
        </div>
      </nav>

      {/* Content */}
      <main className="px-6 lg:px-12 py-12 max-w-4xl mx-auto">
        <h1 className="text-3xl md:text-4xl font-bold mb-8" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Widerrufsbelehrung
        </h1>

        <div className="prose prose-invert max-w-none space-y-6 text-gray-300">
          <p className="text-lg">
            Stand: März 2026
          </p>

          {/* Important Notice Box */}
          <div className="p-6 rounded-2xl border border-yellow-500/30" style={{ backgroundColor: 'rgba(234, 179, 8, 0.1)' }}>
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-bold text-yellow-500 mb-2">Wichtiger Hinweis zum Widerrufsrecht</h3>
                <p className="text-yellow-200/80">
                  <strong>Das Widerrufsrecht erlischt, sobald mit der Ausführung des Auftrags begonnen wurde.</strong> 
                  Mit Ihrer Auftragserteilung und der damit verbundenen Beauftragung eines Freelancers 
                  stimmen Sie dem vorzeitigen Beginn der Leistungserbringung zu.
                </p>
              </div>
            </div>
          </div>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-white">Widerrufsrecht für Verbraucher</h2>
            <p>
              Sie haben das Recht, binnen vierzehn Tagen ohne Angabe von Gründen diesen Vertrag zu widerrufen.
            </p>
            <p>
              Die Widerrufsfrist beträgt vierzehn Tage ab dem Tag des Vertragsabschlusses.
            </p>
            <p>
              Um Ihr Widerrufsrecht auszuüben, müssen Sie uns
            </p>
            <p className="pl-4 border-l-2 border-purple-500">
              RevoCut<br />
              E-Mail: info@revocut-media.de
            </p>
            <p>
              mittels einer eindeutigen Erklärung (z.B. ein mit der Post versandter Brief oder E-Mail) 
              über Ihren Entschluss, diesen Vertrag zu widerrufen, informieren.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-white">Folgen des Widerrufs</h2>
            <p>
              Wenn Sie diesen Vertrag widerrufen, haben wir Ihnen alle Zahlungen, die wir von Ihnen 
              erhalten haben, unverzüglich und spätestens binnen vierzehn Tagen ab dem Tag zurückzuzahlen, 
              an dem die Mitteilung über Ihren Widerruf dieses Vertrags bei uns eingegangen ist.
            </p>
            <p>
              Für diese Rückzahlung verwenden wir dasselbe Zahlungsmittel, das Sie bei der ursprünglichen 
              Transaktion eingesetzt haben, es sei denn, mit Ihnen wurde ausdrücklich etwas anderes vereinbart.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-white text-red-400">Ausschluss des Widerrufsrechts</h2>
            <div className="p-6 rounded-2xl border border-red-500/30" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
              <p className="text-red-200/90">
                <strong>Das Widerrufsrecht besteht nicht bei Verträgen zur Erbringung von Dienstleistungen, 
                wenn der Unternehmer die Dienstleistung vollständig erbracht hat und mit der Ausführung 
                der Dienstleistung erst begonnen hat, nachdem der Verbraucher dazu seine ausdrückliche 
                Zustimmung gegeben hat und gleichzeitig seine Kenntnis davon bestätigt hat, dass er sein 
                Widerrufsrecht bei vollständiger Vertragserfüllung durch den Unternehmer verliert.</strong>
              </p>
            </div>
            <p>
              Gemäß § 356 Abs. 4 BGB erlischt das Widerrufsrecht bei einem Vertrag über die Erbringung 
              von Dienstleistungen, wenn:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Der Unternehmer die Dienstleistung vollständig erbracht hat, und
              </li>
              <li>
                Mit der Ausführung der Dienstleistung erst begonnen hat, nachdem der Verbraucher 
                seine ausdrückliche Zustimmung gegeben hat, und
              </li>
              <li>
                Der Verbraucher gleichzeitig seine Kenntnis davon bestätigt hat, dass er sein 
                Widerrufsrecht bei vollständiger Vertragserfüllung verliert.
              </li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-white">Praktische Bedeutung für Ihre Bestellung</h2>
            <p>
              Bei der Beauftragung von Video-Postproduktionsdienstleistungen über RevoCut bedeutet dies konkret:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Vor Auftragsbeginn:</strong> Sie können innerhalb von 14 Tagen nach Vertragsschluss 
                ohne Angabe von Gründen widerrufen, solange noch nicht mit der Arbeit begonnen wurde.
              </li>
              <li>
                <strong>Nach Auftragsbeginn:</strong> Sobald der zugewiesene Freelancer mit der Bearbeitung 
                Ihres Projekts begonnen hat, erlischt Ihr Widerrufsrecht. Dies geschieht mit Ihrer 
                ausdrücklichen Zustimmung bei der Auftragserteilung.
              </li>
              <li>
                <strong>Teilweise erbrachte Leistungen:</strong> Haben Sie dem Beginn der Leistungserbringung 
                zugestimmt und widerrufen dennoch, sind Sie verpflichtet, für die bis zum Widerruf 
                erbrachten Leistungen anteilig zu zahlen.
              </li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-white">Muster-Widerrufsformular</h2>
            <p>
              Wenn Sie den Vertrag widerrufen wollen, können Sie das folgende Formular verwenden:
            </p>
            <div className="p-6 rounded-2xl border border-white/10" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
              <p className="text-gray-400 italic">
                An:<br />
                RevoCut<br />
                E-Mail: info@revocut-media.de<br /><br />
                
                Hiermit widerrufe(n) ich/wir (*) den von mir/uns (*) abgeschlossenen Vertrag über 
                die Erbringung der folgenden Dienstleistung:<br /><br />
                
                _______________________________________<br /><br />
                
                Bestellt am (*) / erhalten am (*):<br />
                _______________________________________<br /><br />
                
                Name des/der Verbraucher(s):<br />
                _______________________________________<br /><br />
                
                Anschrift des/der Verbraucher(s):<br />
                _______________________________________<br /><br />
                
                Datum:<br />
                _______________________________________<br /><br />
                
                Unterschrift des/der Verbraucher(s) (nur bei Mitteilung auf Papier):<br />
                _______________________________________<br /><br />
                
                (*) Unzutreffendes streichen
              </p>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-white">Kontakt</h2>
            <p>
              Bei Fragen zum Widerrufsrecht kontaktieren Sie uns unter:
            </p>
            <p>
              E-Mail: info@revocut-media.de
            </p>
          </section>
        </div>
      </main>
    </div>
  );
};

export default Widerruf;
